export type EmailAudience = 'customer' | 'support' | 'custom';

export const EMAIL_SIGNATURE_KEY = 'email-signature';

/** Plain-English labels for merge fields shown in the admin UI. */
export const EMAIL_FIELD_LABELS: Record<string, string> = {
  siteHost: 'Website name',
  siteName: 'Store name',
  recipientName: 'Customer name',
  fullName: 'Full name',
  name: 'Name',
  email: 'Email address',
  phone: 'Phone number',
  desk: 'Desk',
  subject: 'Subject',
  message: 'Message',
  country: 'Country',
  state: 'State',
  city: 'City',
  submittedAt: 'Submitted date',
  userIp: 'User IP',
  supportEmail: 'Support email',
  contactEmail: 'Support email',
  referenceLabel: 'Reference label',
  referenceValue: 'Reference detail',
  institutionName: 'Institution name',
  institutionType: 'Institution type',
  designation: 'Designation',
  officialEmail: 'Official email',
  gstin: 'GSTIN',
  factbooks: 'Factbooks',
  formats: 'Formats',
  requiredBy: 'Required by',
  budgetCycle: 'Budget cycle',
  additionalNotes: 'Additional notes',
  address: 'Address',
  landmark: 'Landmark',
  pincode: 'Pincode',
  registeredAt: 'Registered date',
  orderNumber: 'Order number',
  customerName: 'Customer name',
  customerEmail: 'Customer email',
  grandTotal: 'Order total',
  paymentStatus: 'Payment status',
  orderStatus: 'Order status',
  placedAt: 'Order date',
  previousStatusLabel: 'Previous status',
  newStatusLabel: 'New status',
  statusMessage: 'Status message',
  fulfillmentType: 'Fulfillment type',
};

export function getEmailFieldLabel(key: string): string {
  if (EMAIL_FIELD_LABELS[key]) return EMAIL_FIELD_LABELS[key];
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
}

/** Friendly token shown/edited by staff: [Customer name] */
export function toFriendlyFieldToken(key: string): string {
  return `[${getEmailFieldLabel(key)}]`;
}

export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Convert stored EJS/mustache tokens into friendly [Label] for the editor. */
export function storageToFriendlyTemplate(text: string, keys: string[]): string {
  if (!text) return '';
  let next = text;
  const sortedKeys = [...keys].sort((a, b) => b.length - a.length);

  for (const key of sortedKeys) {
    const labelToken = toFriendlyFieldToken(key);
    const ejs = new RegExp(`<%[=-]\\s*${escapeRegExp(key)}\\s*%>`, 'g');
    const mustache = new RegExp(`\\{\\{\\s*${escapeRegExp(key)}\\s*\\}\\}`, 'g');
    next = next.replace(ejs, labelToken).replace(mustache, labelToken);
  }

  return next;
}

/** Convert friendly [Label] tokens back to <%= key %> for storage. */
export function friendlyToStorageTemplate(text: string, keys: string[]): string {
  if (!text) return '';
  let next = text;
  const pairs = keys
    .map((key) => ({ key, label: getEmailFieldLabel(key) }))
    .sort((a, b) => b.label.length - a.label.length);

  for (const { key, label } of pairs) {
    const pattern = new RegExp(`\\[\\s*${escapeRegExp(label)}\\s*\\]`, 'gi');
    next = next.replace(pattern, `<%= ${key} %>`);
  }

  return next;
}

/** Sample values for the live preview panel. */
export const EMAIL_PREVIEW_SAMPLE: Record<string, string> = {
  siteHost: 'Indiastatpublications.com',
  siteName: 'Indiastat Publications',
  recipientName: 'Priya Sharma',
  fullName: 'Priya Sharma',
  name: 'Priya Sharma',
  email: 'priya.sharma@example.com',
  phone: '+91 98765 43210',
  desk: 'Sales',
  subject: 'Need pricing for District Factbook',
  message: 'Please share the latest pricing and availability.',
  country: 'India',
  state: 'Maharashtra',
  city: 'Mumbai',
  submittedAt: '7/31/2026, 12:30:00 PM',
  userIp: '103.21.44.10',
  supportEmail: 'support@indiastatpublications.com',
  contactEmail: 'support@indiastatpublications.com',
  referenceLabel: 'Your subject',
  referenceValue: 'Need pricing for District Factbook',
  institutionName: 'University of Mumbai Library',
  institutionType: 'University',
  designation: 'Librarian',
  officialEmail: 'library@mu.ac.in',
  gstin: '27AAAAA0000A1Z5',
  factbooks: 'Maharashtra District Factbook (qty 2)',
  formats: 'Print, eFlipBook',
  requiredBy: '2026-09-01',
  budgetCycle: 'FY 2026-27',
  additionalNotes: 'Please include institutional pricing.',
  address: '12 Marine Drive',
  landmark: 'Near Oval Maidan',
  pincode: '400020',
  registeredAt: '7/31/2026, 11:00:00 AM',
  orderNumber: 'ISP-10428',
  customerName: 'Priya Sharma',
  customerEmail: 'priya.sharma@example.com',
  grandTotal: '₹12,450.00',
  paymentStatus: 'Paid',
  orderStatus: 'Confirmed',
  placedAt: '7/30/2026, 4:15:00 PM',
  previousStatusLabel: 'Order Placed',
  newStatusLabel: 'Order Accepted',
  statusMessage: 'Great news! Your order has been accepted and will be prepared shortly.',
  fulfillmentType: 'eFlipBook',
};

export function renderFriendlyPreview(
  template: string,
  keys: string[],
  sample: Record<string, string> = EMAIL_PREVIEW_SAMPLE,
): string {
  if (!template) return '';
  let next = storageToFriendlyTemplate(template, keys);
  const pairs = keys
    .map((key) => ({ key, label: getEmailFieldLabel(key), value: sample[key] ?? '—' }))
    .sort((a, b) => b.label.length - a.label.length);

  for (const { label, value } of pairs) {
    const pattern = new RegExp(`\\[\\s*${escapeRegExp(label)}\\s*\\]`, 'gi');
    next = next.replace(pattern, value);
  }

  next = next
    .replace(/<%[-=]\s*([a-zA-Z0-9_]+)\s*%>/g, (_m, key: string) => sample[key] ?? '—')
    .replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key: string) => sample[key] ?? '—');

  return next;
}

export interface EmailTemplate {
  id: string;
  key: string;
  name: string;
  description: string;
  subject: string;
  htmlBody: string;
  textBody: string;
  availableVariables: string[];
  audience: EmailAudience;
  isSystem: boolean;
  includeSignature: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface EmailTemplatesResponse {
  results: EmailTemplate[];
}

export interface CreateEmailTemplatePayload {
  name: string;
  description?: string;
  subject: string;
  htmlBody?: string;
  audience?: EmailAudience;
  includeSignature?: boolean;
  isActive?: boolean;
}

export interface UpdateEmailTemplatePayload {
  name?: string;
  description?: string;
  subject?: string;
  htmlBody?: string;
  textBody?: string;
  audience?: EmailAudience;
  includeSignature?: boolean;
  isActive?: boolean;
}
